import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/button';
import bg1 from '../assets/images/bg1.png';
import { Shield, Zap, PiggyBank, ArrowRight, Play, Tv, Film, Check, Sparkles } from 'lucide-react';

export default function HomePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const features = [
    {
      icon: Shield,
      title: t('features.secure.title'),
      description: t('features.secure.description')
    },
    {
      icon: PiggyBank,
      title: t('features.affordable.title'),
      description: t('features.affordable.description')
    },
    {
      icon: Zap,
      title: t('features.instant.title'),
      description: t('features.instant.description')
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" }
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      
      <main className="flex-1">
        {/* Hero Section */}
        <section className="relative overflow-hidden py-16 md:py-24 lg:py-32">
          {/* Background gradient */}
          <div className="absolute inset-0 bg-gradient-to-b from-primary/5 via-transparent to-transparent" />
          
          {/* Floating shapes */}
          <div className="absolute top-20 right-10 w-72 h-72 bg-primary/5 rounded-full blur-3xl animate-float" />
          <div className="absolute bottom-20 left-10 w-96 h-96 bg-primary/3 rounded-full blur-3xl animate-float" style={{ animationDelay: '1s' }} />
          
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div 
              className="max-w-4xl mx-auto text-center"
              initial="hidden"
              animate="visible"
              variants={containerVariants}
            >
              {/* Badge */}
              <motion.div variants={itemVariants} className="flex justify-center mb-8">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-full">
                  <Sparkles className="h-4 w-4 text-primary" strokeWidth={2} />
                  <span className="text-sm font-bold text-primary">Nouveau en Haïti</span>
                </div>
              </motion.div>

              {/* Headline */}
              <motion.h1 
                variants={itemVariants}
                className="font-heading text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black leading-[1.1] mb-6 tracking-tight"
              >
                {t('hero.title')}
              </motion.h1>
              
              <motion.p 
                variants={itemVariants}
                className="text-lg md:text-xl text-muted-foreground mb-10 max-w-2xl mx-auto font-medium"
              >
                {t('hero.subtitle')}
              </motion.p>

              {/* Platform badges */}
              <motion.div variants={itemVariants} className="flex flex-wrap justify-center gap-3 mb-10">
                {['Netflix', 'Disney+', 'Spotify', 'HBO Max'].map((platform, i) => (
                  <div key={platform} className="flex items-center gap-2 px-4 py-2 bg-white rounded-full shadow-soft border border-border/50">
                    <div className="w-6 h-6 bg-gradient-to-br from-primary/20 to-primary/5 rounded-full flex items-center justify-center">
                      {i === 0 && <Tv className="h-3 w-3" strokeWidth={2} />}
                      {i === 1 && <Play className="h-3 w-3" strokeWidth={2} />}
                      {i === 2 && <Film className="h-3 w-3" strokeWidth={2} />}
                      {i === 3 && <Tv className="h-3 w-3" strokeWidth={2} />}
                    </div>
                    <span className="text-sm font-bold">{platform}</span>
                  </div>
                ))}
              </motion.div>
              
              {/* CTAs */}
              <motion.div variants={itemVariants} className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  size="lg" 
                  className="rounded-full px-8 py-6 text-base shadow-strong hover:shadow-xl"
                  onClick={() => navigate('/register')}
                  data-testid="hero-cta-btn"
                >
                  {t('hero.cta')}
                  <ArrowRight className="h-5 w-5 ml-1" strokeWidth={2} />
                </Button>
                <Button 
                  variant="outline" 
                  size="lg"
                  className="rounded-full px-8 py-6 text-base"
                  onClick={() => navigate('/pricing')}
                  data-testid="hero-pricing-btn"
                >
                  {t('hero.learnMore')}
                </Button>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-16 md:py-24 bg-gradient-to-b from-muted/50 to-background">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div 
              className="text-center mb-16"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-black mb-4">
                {t('features.title')}
              </h2>
            </motion.div>

            <div className="grid md:grid-cols-3 gap-6 md:gap-8">
              {features.map((feature, index) => (
                <motion.div
                  key={index}
                  className="bg-white rounded-3xl p-8 shadow-soft border-2 border-transparent hover:border-primary/20 hover:shadow-medium transition-all duration-300"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <div className="w-16 h-16 bg-gradient-to-br from-primary/10 to-primary/5 rounded-2xl flex items-center justify-center mb-6">
                    <feature.icon className="h-8 w-8 text-primary" strokeWidth={1.5} />
                  </div>
                  <h3 className="font-heading text-xl font-bold mb-3">
                    {feature.title}
                  </h3>
                  <p className="text-muted-foreground font-medium">
                    {feature.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* How it works */}
        <section className="py-16 md:py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
              >
                <div className="relative">
                  <div className="absolute -inset-4 bg-gradient-to-r from-primary/20 to-primary/5 rounded-3xl blur-2xl" />
                    <img 
                      src={bg1}
                      alt="Happy Haitian Girl"
                      className="relative w-full aspect-[4/3] object-cover rounded-3xl shadow-strong"
                    />
                </div>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
              >
                <h2 className="font-heading text-3xl md:text-4xl font-black mb-6">
                  Simple comme Bonjour !
                </h2>
                <div className="space-y-6">
                  {[
                    { step: '1', title: 'Créez votre compte', desc: 'Inscription gratuite en moins de 2 minutes' },
                    { step: '2', title: 'Rechargez votre portefeuille', desc: 'Via MonCash, simple et sécurisé' },
                    { step: '3', title: 'Choisissez vos abonnements', desc: 'Accès immédiat aux identifiants' }
                  ].map((item, i) => (
                    <div key={i} className="flex gap-4">
                      <div className="flex-shrink-0 w-12 h-12 bg-primary text-white rounded-2xl flex items-center justify-center font-heading font-black text-lg">
                        {item.step}
                      </div>
                      <div>
                        <h3 className="font-heading font-bold text-lg mb-1">{item.title}</h3>
                        <p className="text-muted-foreground font-medium">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <Button 
                  className="mt-8 rounded-full px-8" 
                  size="lg"
                  onClick={() => navigate('/register')}
                >
                  Commencer maintenant
                </Button>
              </motion.div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 md:py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              className="relative bg-primary text-primary-foreground rounded-[2.5rem] p-10 md:p-16 overflow-hidden"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              {/* Background decoration */}
              <div className="absolute top-0 right-0 w-96 h-96 bg-white/5 rounded-full blur-3xl" />
              <div className="absolute bottom-0 left-0 w-72 h-72 bg-white/5 rounded-full blur-3xl" />
              
              <div className="relative text-center max-w-2xl mx-auto">
                <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-black mb-6">
                  Prêt à économiser?
                </h2>
                <p className="text-lg md:text-xl opacity-90 mb-8 font-medium">
                  Rejoignez des milliers d'utilisateurs qui économisent sur leurs abonnements streaming.
                </p>
                <Button 
                  size="lg" 
                  variant="secondary" 
                  className="rounded-full px-10 py-6 text-base font-bold shadow-strong"
                  onClick={() => navigate('/register')}
                >
                  {t('nav.register')}
                  <ArrowRight className="h-5 w-5 ml-2" strokeWidth={2} />
                </Button>
              </div>
            </motion.div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
